/**
 * Email-Automatisierungen — Entscheidungs-Board (Wolfgang + Prozess-Rollen
 * SCM/Logistik/EF-Auftragsabwicklung/Einkauf, Rolle aus dem SF-Roster).
 *
 * L1 (Landing): Segment-Umschalter mit GENAU 2 Top-Level-Views —
 *   „Entscheiden & Steuern" (das Produkt: Rollup + Voraussetzungen + Quick-Wins +
 *   Prozess-Familien als Detailebene) und „Agent-Roadmap" (Reifegrad-Kontext).
 *   view= Query-Param. Vorfall-Ketten (jetzt Beleg-Block in der Familien-L2) und
 *   Dokumente (jetzt Kontext-Panel in der Karten-L2) sind KEINE Top-Level-Views
 *   mehr; alte URLs (view=vorfaelle|dokumente|familien) fallen sauber auf
 *   „Entscheiden" zurueck. Views + L2s in EmailAutomationDecide.tsx. Der fruehere
 *   Use-Case-Katalog (89er-Liste) ist aus der UI entfernt; die Katalog-L2
 *   (/email-automation/:useCaseId) bleibt fuer Deep-Links erhalten.
 * L2 (Route /email-automation/:useCaseId): Header · Empfehlung (mit Pattern-
 *   Ablauf-Grafik) · Bestand heute · Evidenz (Member-Cluster + Counterparties) ·
 *   Beispiele quote-first (Blockquote prominent, Anhang inline nur wenn da).
 *
 * Backend: app/routers/email_automation.py (/email-automation). Der ZUGANG
 * haengt unveraendert am serverseitigen Rollen-Gate (403 fuer Nicht-Berechtigte).
 * Die NAV folgt dieser Logik seit #81 (2026-07-31) NICHT mehr: der Menue-Eintrag
 * steht nur noch fuer Wolfgang (frontend/src/studioAccess.ts, reine Kosmetik),
 * `GET /email-automation/access` fragt das Frontend dafuer nicht mehr ab — der
 * Endpunkt selbst bleibt bestehen. Rollen-Kollegen erreichen die Seite also
 * weiterhin per URL, sehen sie aber nicht im Menue (so gewollt).
 * Detail-Navigation via React-Router (echte URLs) → Browser-Back funktioniert.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Layers,
  ListFilter,
  Loader2,
  Plus,
  Quote,
  Tag,
  Trash2,
} from "lucide-react";
import clsx from "clsx";
import { api, apiUrl, authToken, ApiError } from "../api";
import { Card, Chip, Field, Input, Modal, useModal } from "../ui";
import { useToast } from "../ui/toast";
import { BoardError, BoardSkeleton, DecideDetailView, useDecisionBoard } from "./EmailAutomationDecide";
import {
  EmailAutomationHierarchy,
  FilterBar,
  SEGMENTS,
  buildFilterQs,
  l1Path,
  normSeg,
  type Seg,
} from "./EmailAutomationHierarchy";

// ── Typen (Spiegel der Pydantic-Schemas) ─────────────────────────────────────
export interface Coverage {
  status?: string | null;
  existing?: string | null;
  recommendation?: string | null;
}
interface CoverageFull extends Coverage {
  key?: string | null;
  platform?: string | null;
  pattern?: string | null;
  effort?: string | null;
  quick_win?: boolean | null;
  matched_on?: string | null;
}
interface MemberCluster {
  id?: string; name?: string; name_de?: string; freq?: number; hours_6mo?: number; act?: string;
  act_label?: string; coverage?: Coverage | null;
  // Aufwand HEUTE (volle Handling-Zeit) je Muster; hours_6mo = davon automatisierbar.
  handling_hours_6mo?: number | null;
}
interface UseCaseOverview {
  id: string;
  use_case_key: string;
  name: string | null;
  dept: string | null;
  lead: string | null;
  artifact_family: string | null;
  human_only: boolean | null;
  sonstige: boolean | null;
  pattern: string | null;
  platform: string | null;
  effort: string | null;
  quick_win: boolean | null;
  freq_6mo: number | null;
  hours_6mo: number | null;
  hours_per_year: number | null;
  coverage: Coverage | null;
  coverage_full: CoverageFull | null;
  counterparties: string[] | null;
}
export interface Attachment {
  id: string; filename: string | null; content_type: string | null;
  size: number | null; fetched: boolean;
}
export interface Example {
  id: string; seq: number | null; quote: string | null; subject: string | null;
  occurred_at: string | null; direction: string | null; mailbox: string | null;
  thread_id: string | null; sf_thread_summary: string | null;
  member_cluster_id: string | null; member_cluster_name: string | null;
  attachments: Attachment[];
}
export interface UseCaseDetail extends UseCaseOverview {
  description: string | null;
  act_mix: Record<string, number> | null;
  act_labels: Record<string, string> | null;
  member_clusters: MemberCluster[] | null;
  examples: Example[];
}

// ── Plattform-Farbcodierung (feste Palette, dark-aware) ───────────────────────
const PLATFORM_STYLE: Record<string, string> = {
  "SF-Flow": "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300",
  Atlas: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
  Portal: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300",
  "Email-Service+Atlas": "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300",
  "Bleibt manuell": "bg-gray-200 text-gray-700 dark:bg-gray-500/15 dark:text-gray-300",
};
function PlatformBadge({ platform }: { platform: string | null }) {
  const p = platform || "—";
  const cls = PLATFORM_STYLE[p] ?? "bg-surface-3 text-text-2";
  return (
    <span className={clsx("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap", cls)}>
      {p}
    </span>
  );
}

// ── Bestand-Status (v8: DS-Chip statt Ampel-Glyph — Sprache wie Bündel-BEST.) ──
function ampelInfo(status: string | null | undefined): { tone: "success" | "petrol" | "neutral"; short: string; label: string } {
  const s = (status || "").toLowerCase();
  if (s === "covered_auto" || s === "covered_manual")
    return { tone: "success", short: "abgedeckt", label: s === "covered_auto" ? "Automatisch abgedeckt" : "Manuell abgedeckt" };
  if (s === "partial") return { tone: "petrol", short: "teilweise", label: "Teilweise abgedeckt" };
  return { tone: "neutral", short: "offen", label: "Nicht abgedeckt" };
}
export function Ampel({ coverage }: { coverage: Coverage | null }) {
  const { tone, short, label } = ampelInfo(coverage?.status);
  const title = coverage?.existing ? `${label} — ${coverage.existing}` : label;
  return (
    <Chip variant="status" tone={tone} title={title}>
      {short}
    </Chip>
  );
}

// ── Aufwand-Badge (S/M/L) — neutral (Farbdisziplin: Effort ist keine Ampel) ────
export function EffortBadge({ effort }: { effort: string | null }) {
  const e = (effort || "").toUpperCase();
  return <span className="inline-grid place-items-center min-w-[1.25rem] px-1 py-0.5 rounded-chip border border-border-strong text-[0.7rem] font-semibold tabular-nums text-text-2" title={`Aufwand ${e || "?"}`}>{e || "—"}</span>;
}
function DeptChip({ dept }: { dept: string | null }) {
  return <span className="px-2 py-0.5 rounded-md bg-surface-3 text-text-2 text-xs whitespace-nowrap">{dept ?? "—"}</span>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE");
}
function fmtBytes(n: number | null): string {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function extOf(name: string | null): string { return (name || "").toLowerCase().split(".").pop() || ""; }
const IMG_EXT = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];

// SF-Durchgriff: Thread__c-Record-Id → Salesforce-Deeplink. Basis aus dem Board
// (server-gebaute sf_url zeigen alle auf https://bitop.my.salesforce.com/{id});
// per Env überschreibbar. Wird für UC-Belege genutzt, deren Rohdaten nur die
// thread_id (SF-Record-Id) tragen.
export const SF_BASE =
  (import.meta.env.VITE_SF_BASE as string | undefined) ?? "https://bitop.my.salesforce.com";
export function sfThreadUrl(threadId: string | null | undefined): string | null {
  const id = (threadId || "").trim();
  return id ? `${SF_BASE}/${id}` : null;
}

// Pattern-Ablauf (3–4 Schritte je P1–P5) — statische Darstellung.
const PATTERN_FLOW: Record<string, string[]> = {
  P1: ["Auslöser (SF-Event)", "Flow generiert Dokument/Mail", "Automatischer Versand"],
  P2: ["Eingang (Mail/Portal)", "LLM liest & extrahiert", "Prüfung", "Schreibt nach SF"],
  P3: ["Initiale Anfrage", "Follow-up-Task + Auto-Reminder", "Auto-Close bei Antwort"],
  P4: ["Self-Serve-Portal", "Gegenpartei erledigt selbst", "SF wird direkt befüllt"],
  P5: ["Eingang", "FAQ-/Wissens-Antwort (Atlas)", "Manuelle Freigabe", "Versand"],
};
const PATTERN_LABEL: Record<string, string> = {
  P1: "P1 — SF-Flow: erzeugen & senden",
  P2: "P2 — Inbound: LLM liest & prüft",
  P3: "P3 — Chase: nachfassen & auto-schließen",
  P4: "P4 — Self-Serve-Portal",
  P5: "P5 — FAQ/Wissen + Freigabe",
};

// act-Code → deutsches Label. Primär aus dem Dataset (member.act_label bzw.
// detail.act_labels); dieses Fallback greift, solange das Dataset das Mapping
// noch nicht mitliefert.
const ACT_LABEL_FALLBACK: Record<string, string> = {
  generate_doc: "Dokument erstellen/senden",
  provide_data: "Daten/Unterlagen bereitstellen",
  see_status: "Status mitteilen",
  chase: "Nachfassen",
  review_expert: "Fachlich prüfen",
  decide: "Entscheiden/Freigeben",
  validate: "Prüfen/Bestätigen",
  reconcile: "Abgleichen/Klären",
  route: "Weiterleiten",
  process_question: "Frage beantworten",
  ack: "Eingang bestätigen",
  remind_internal: "Intern erinnern",
  consult_keyaccount: "Key-Account beraten",
  sign: "Unterschreiben",
  not_automatable: "Nicht automatisierbar",
};
/** Deutsches Label für einen act-Code auflösen: member.act_label →
 *  detail.act_labels[code] → lokales Fallback → Code selbst. */
function actLabel(
  code: string | null | undefined,
  memberLabel?: string | null,
  labels?: Record<string, string> | null,
): string {
  if (memberLabel) return memberLabel;
  const c = (code || "").trim();
  if (!c) return "—";
  return (labels && labels[c]) || ACT_LABEL_FALLBACK[c] || c;
}
function ActLabelChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-surface-3 text-text-2 text-[0.7rem] font-medium whitespace-nowrap">
      {label}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Root: Route-Dispatch.
//   /email-automation/decide/:cardId  → DecideDetailView (L2 einer Entscheidungs-/
//                                        Prereq-/Familien-/Agent-Stufen-Karte)
//   /email-automation/:useCaseId      → UseCaseDetailView (Katalog-L2, Deep-Links)
//   /email-automation                 → Overview-Shell mit Segment-Umschalter
//                                        (view=entscheiden|familien|vorfaelle|agent)
// ═════════════════════════════════════════════════════════════════════════════
// Route-Params des Tabs (alle optional — je nach Route belegt).
type EaParams = {
  prozessId?: string; docTyp?: string; leverId?: string; stufeNr?: string;
  clusterId?: string; cardId?: string; useCaseId?: string; ucKey?: string;
};

// Aktive Ansicht aus der Route ableiten — markiert den richtigen Tab auf JEDER
// Ebene. v9: drei Ansichten (Aufgaben · Automatisierungen · Entscheiden). Prozess-
// Detail lebt unter Aufgaben; Automatisierungs-Detail + Alt-Hebel/Roadmap-Routen
// unter Automatisierungen; Entscheidungs-Karten unter Entscheiden.
function activeSeg(params: EaParams, segParam: string | null): Seg {
  if (params.prozessId) return "aufgaben";
  if (params.ucKey || params.leverId || params.stufeNr || params.clusterId) return "automatisierungen";
  if (params.cardId) return "entscheiden";
  return normSeg(segParam);
}

// ═════════════════════════════════════════════════════════════════════════════
// Persistente App-Shell — Kopfband (Titel · Segment-Tabs · Filter) bleibt auf
// ALLEN Routen stehen (L1/L2/L3 · /decide · UC-Detail); nur der Content-Outlet
// darunter wechselt. Loest die Kern-Beschwerde „Kopf-Menue verschwindet im
// Drill-Down" (Audit Befund 1). Segment-Tabs + Filter sind aus jeder Tiefe
// bedienbar; Scroll wird pro Routenwechsel zurueckgesetzt.
// ═════════════════════════════════════════════════════════════════════════════
export default function EmailAutomationPage() {
  const params = useParams() as EaParams;
  const navigate = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0, left: 0 }); }, [location.pathname]);

  const dept = sp.get("dept");
  const lever = sp.get("lever");
  // Regeln/Filter (P2.14) ist eine kleine Utility-Unterseite neben den drei
  // Analyse-Segmenten (kein 4. Segment): eigener Pfad, eigener Outlet.
  const isRegeln = /\/email-automation\/regeln\/?$/.test(location.pathname);
  const onL1 = !params.prozessId && !params.leverId && !params.stufeNr &&
    !params.clusterId && !params.cardId && !params.useCaseId && !params.ucKey;
  const seg = activeSeg(params, sp.get("seg") ?? sp.get("view"));

  const switchSeg = (s: Seg) => {
    navigate(l1Path(buildFilterQs(s, dept, lever)), onL1 ? { replace: true } : undefined);
  };
  const toggleFilter = (key: "dept" | "lever", val: string) => {
    const u = new URLSearchParams(sp);
    if (u.get(key) === val) u.delete(key); else u.set(key, val);
    setSp(u, { replace: true });   // Filter-Wechsel: kein History-Muell
  };

  return (
    <div className="ea-tab h-full flex flex-col min-h-0">
      {/* Kopfband — bleibt stehen (shrink-0), scrollt NICHT mit dem Content */}
      <div className="shrink-0 border-b border-border bg-surface">
        <div className="px-4 md:px-6 pt-3.5 pb-2 flex items-center gap-2 flex-wrap">
          <Layers className="w-5 h-5 text-brand-500 shrink-0" />
          <h1 className="ea-apptitle text-text">Email-Automatisierungen</h1>
          <span className="ea-meta text-text-3 hidden sm:inline">Steuerung · 6-Monats-Analyse · EF / SCM / Logistics / Purchasing</span>
        </div>
        <div className="px-4 md:px-6 pb-2 flex items-center gap-3">
          {/* Bei 5 Segmenten passt die Leiste unter md nicht mehr in die Zeile →
              horizontal scrollbar HALTEN (nicht clippen), sonst waere der letzte
              Tab „Aufgaben" auf dem Handy nicht antippbar. Kein Seiten-Scroll:
              der overflow lebt im breitenbegrenzten Wrapper. */}
          <div className="overflow-x-auto flex-1 min-w-0">
            <div className="inline-flex w-max gap-1 text-sm" role="tablist" aria-label="Ansicht">
              {SEGMENTS.map(([v, label]) => (
                <button key={v} type="button" role="tab" aria-selected={!isRegeln && seg === v}
                  onClick={() => switchSeg(v)}
                  className={clsx(
                    "px-3.5 py-2 shrink-0 whitespace-nowrap font-medium border-b-2 transition-colors rounded-t-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                    !isRegeln && seg === v
                      ? "border-brand-500 text-brand-700 dark:text-brand-300 font-semibold"
                      : "border-transparent text-text-2 hover:text-text hover:bg-surface-2",
                  )}>{label}</button>
              ))}
            </div>
          </div>
          {/* Dezenter Einstieg in die Posteingangsregeln (P2.14) — sichtbar aus
              jeder Tiefe, aber kein Analyse-Segment. */}
          <Link to="/email-automation/regeln"
            aria-current={isRegeln ? "page" : undefined}
            className={clsx(
              "shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              isRegeln
                ? "border-brand-500 text-brand-700 dark:text-brand-300 bg-brand-50 dark:bg-brand-900/20"
                : "border-border text-text-2 hover:text-text hover:bg-surface-2",
            )}>
            <ListFilter className="w-3.5 h-3.5" /> Regeln
          </Link>
        </div>
        {!isRegeln && (
          <div className="px-4 md:px-6 pb-2.5">
            <FilterBar seg={seg} dept={dept} lever={lever} onToggle={toggleFilter} />
          </div>
        )}
      </div>

      {/* Content-Outlet — der EINZIGE Bereich, der beim Drill wechselt */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 min-h-0">
        {isRegeln ? <RulesView /> : <ShellOutlet params={params} />}
      </div>
    </div>
  );
}

// Outlet-Dispatch: Decide-Karte · Drill/L1-Hierarchie · UC-Detail.
function ShellOutlet({ params }: { params: EaParams }) {
  const { cardId, useCaseId, prozessId, leverId, stufeNr, clusterId, ucKey } = params;
  if (cardId) return <DecideDetailView cardId={cardId} />;
  if (prozessId || leverId || stufeNr || clusterId || ucKey) return <HierarchyOutlet />;
  if (useCaseId) return <UseCaseDetailView id={useCaseId} />;
  return <HierarchyOutlet />;
}

// Laedt das Board (Skeleton statt Spinner, Fehler mit Retry) und rendert die
// Prozess-Hierarchie (L1-Content + Drill-Dispatch).
function HierarchyOutlet() {
  const { data, error, reload } = useDecisionBoard(true);
  if (error) return <BoardError error={error} onRetry={reload} />;
  if (data === null) return <BoardSkeleton />;
  return <EmailAutomationHierarchy board={data} />;
}

// ═════════════════════════════════════════════════════════════════════════════
// Posteingangsregeln / Filter (P2.14) — kleine aktive Regel-Verwaltung.
// Serverseitige Outlook-Regeln (Graph mailFolders/inbox/messageRules); Atlas
// speichert nichts lokal. Drei Zustaende wie P2.12: 403 → „Postfach nicht
// freigeschaltet", 502 → Info „derzeit nicht verfuegbar", sonst die Liste.
// R1-Aktionen bewusst klein + nicht-versteckend: Als-gelesen + Kategorie-Label.
// ═════════════════════════════════════════════════════════════════════════════
export interface MessageRule {
  id: string;
  displayName?: string | null;
  sequence?: number | null;
  isEnabled?: boolean | null;
  conditions?: { senderContains?: string[]; subjectContains?: string[] } | null;
  actions?: { markAsRead?: boolean; assignCategories?: string[] } | null;
}
type RulesLoad = "loading" | "ok" | "not_enabled" | "unavailable" | "error";

function ruleConditionText(r: MessageRule): string {
  const out: string[] = [];
  const s = r.conditions?.senderContains ?? [];
  const b = r.conditions?.subjectContains ?? [];
  if (s.length) out.push(`Absender enthält „${s.join("“, „")}“`);
  if (b.length) out.push(`Betreff enthält „${b.join("“, „")}“`);
  return out.join(" · ") || "—";
}

function RulesView() {
  const toast = useToast();
  const modal = useModal();
  const [rules, setRules] = useState<MessageRule[] | null>(null);
  const [state, setState] = useState<RulesLoad>("loading");
  const [errText, setErrText] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    try {
      const data = await api.get<{ rules: MessageRule[] }>("/mail/rules");
      setRules(data.rules ?? []);
      setState("ok");
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) { setState("not_enabled"); return; }
      if (e instanceof ApiError && e.status === 502) { setState("unavailable"); return; }
      setErrText(e instanceof ApiError ? e.message : "Regeln konnten nicht geladen werden.");
      setState("error");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onDelete = async (r: MessageRule) => {
    const ok = await modal.confirm({
      title: "Regel löschen?",
      description:
        `„${r.displayName || "Unbenannte Regel"}" wird endgültig gelöscht. ` +
        "Outlook-Regeln haben keinen Papierkorb — das lässt sich nicht rückgängig machen.",
      confirmLabel: "Endgültig löschen",
      destructive: true,
    });
    if (!ok) return;
    const prev = rules ?? [];
    setRules(prev.filter((x) => x.id !== r.id)); // optimistisch entfernen
    try {
      await api.delete(`/mail/rules/${encodeURIComponent(r.id)}`);
      toast.ok("Regel gelöscht");
    } catch (e) {
      setRules(prev); // still zurueckrollen
      toast.err("Löschen fehlgeschlagen", e instanceof ApiError ? e.message : undefined);
    }
  };

  const onCreated = (created: MessageRule) => {
    setRules((cur) => [...(cur ?? []), created]);
    setCreating(false);
    toast.ok("Regel angelegt");
  };

  const header = (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div>
        <h2 className="text-sm font-semibold text-text">Posteingangsregeln</h2>
        <p className="text-xs text-text-3 mt-0.5">
          Laufen serverseitig in Outlook — auch wenn Atlas geschlossen ist. Aktion bewusst
          klein: als gelesen markieren oder ein Kategorie-Label setzen (kein Verschieben).
        </p>
      </div>
      {state === "ok" && (
        <button type="button" onClick={() => setCreating(true)}
          data-testid="rule-new"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500">
          <Plus className="w-4 h-4" /> Neue Regel
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-3xl" data-rules-state={state}>
      {header}
      <Card padding="none" className="p-4">
        {state === "loading" && (
          <div className="flex items-center gap-2 text-sm text-text-3 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Regeln werden geladen …
          </div>
        )}

        {state === "not_enabled" && (
          <div className="py-8 text-center" data-rules-notenabled>
            <p className="text-sm font-medium text-text">Postfach nicht freigeschaltet</p>
            <p className="text-xs text-text-3 mt-1 max-w-md mx-auto">
              Für dein Postfach ist die Regel-Verwaltung nicht aktiviert. Sobald das Postfach
              freigeschaltet ist, kannst du hier Posteingangsregeln sehen und anlegen.
            </p>
          </div>
        )}

        {state === "unavailable" && (
          <div className="py-8 text-center" data-rules-unavailable>
            <p className="text-sm font-medium text-text">Regeln derzeit nicht verfügbar</p>
            <p className="text-xs text-text-3 mt-1 max-w-md mx-auto">
              Die Postfach-Einstellungen (Regeln) sind serverseitig noch nicht freigeschaltet.
              Das ist eine einmalige Admin-Freigabe — sobald sie erteilt ist, erscheinen deine
              Regeln hier automatisch.
            </p>
          </div>
        )}

        {state === "error" && (
          <div className="py-8 text-center">
            <p className="text-sm text-danger">{errText}</p>
            <button type="button" onClick={load}
              className="mt-3 text-sm text-brand-600 hover:underline">Erneut versuchen</button>
          </div>
        )}

        {state === "ok" && (rules?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-3 text-left text-xs border-b border-border">
                  <th className="py-1.5 pr-3 font-medium">Name</th>
                  <th className="py-1.5 px-3 font-medium">Wenn</th>
                  <th className="py-1.5 px-3 font-medium">Dann</th>
                  <th className="py-1.5 pl-3 font-medium text-right w-16">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id} data-rule-row className="border-b border-border/60 last:border-0 align-top">
                    <td className="py-2 pr-3 font-medium text-text">{r.displayName || "Unbenannt"}</td>
                    <td className="py-2 px-3 text-text-2">{ruleConditionText(r)}</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {r.actions?.markAsRead && <Chip>Als gelesen markieren</Chip>}
                        {(r.actions?.assignCategories ?? []).map((c) => (
                          <Chip key={c}><Tag className="w-3 h-3 mr-1 inline" />{c}</Chip>
                        ))}
                        {!r.actions?.markAsRead && !(r.actions?.assignCategories ?? []).length && (
                          <span className="text-text-3 text-xs">—</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 pl-3 text-right">
                      <button type="button" onClick={() => onDelete(r)} data-rule-delete
                        aria-label={`Regel „${r.displayName || "Unbenannt"}" löschen`}
                        className="p-1.5 rounded-md text-text-3 hover:text-danger hover:bg-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-8 text-center" data-rules-empty>
            <p className="text-sm text-text-2">Noch keine Regeln.</p>
            <p className="text-xs text-text-3 mt-1">Lege mit „Neue Regel" die erste an.</p>
          </div>
        ))}
      </Card>

      {creating && <NewRuleModal onClose={() => setCreating(false)} onCreated={onCreated} />}
    </div>
  );
}

// Zentriertes „Neue Regel"-Formular (reused <Modal>: top-1/2 -translate,
// animate-modal-in nur opacity). Eine Bedingung {Absender|Betreff enthält} +
// Freitext, Aktionen {Als gelesen markieren, Kategorie zuweisen}. Kein Ordner-
// Picker (Auto-Label statt Auto-Move). Speichern erst valide → sonst disabled.
function NewRuleModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (r: MessageRule) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [condType, setCondType] = useState<"sender" | "subject">("sender");
  const [condText, setCondText] = useState("");
  const [markRead, setMarkRead] = useState(true);
  const [useCategory, setUseCategory] = useState(false);
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);

  const nameOk = name.trim().length > 0;
  const condOk = condText.trim().length > 0;
  const catOk = !useCategory || category.trim().length > 0;
  const actionOk = markRead || (useCategory && category.trim().length > 0);
  const canSave = nameOk && condOk && actionOk && catOk && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    const body = {
      display_name: name.trim(),
      sender_contains: condType === "sender" ? [condText.trim()] : [],
      subject_contains: condType === "subject" ? [condText.trim()] : [],
      mark_as_read: markRead,
      categories: useCategory && category.trim() ? [category.trim()] : [],
    };
    try {
      const res = await api.post<{ status: string; rule: MessageRule }>("/mail/rules", body);
      onCreated(res.rule);
    } catch (e) {
      setSaving(false);
      toast.err("Regel anlegen fehlgeschlagen", e instanceof ApiError ? e.message : undefined);
    }
  };

  const radio = (val: "sender" | "subject", label: string) => (
    <label className={clsx(
      "flex-1 flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors",
      condType === val ? "border-accent-500 bg-accent-50 dark:bg-accent-900/20 text-text"
                       : "border-border text-text-2 hover:bg-surface-2",
    )}>
      <input type="radio" name="cond" className="accent-accent-500" checked={condType === val}
        onChange={() => setCondType(val)} />
      {label}
    </label>
  );

  return (
    <Modal open onClose={onClose} size="md" icon={<ListFilter className="w-4 h-4" />}
      title="Neue Regel"
      description="Läuft serverseitig in Outlook."
      footer={
        <>
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-3 text-text">
            Abbrechen
          </button>
          <button type="button" onClick={submit} disabled={!canSave} data-rule-save
            className="px-3 py-1.5 text-sm rounded-md bg-accent-500 hover:bg-accent-600 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Regel speichern
          </button>
        </>
      }>
      <div className="space-y-4">
        <Field label="Name der Regel">
          <Input value={name} onChange={(e) => setName(e.target.value)} data-rule-name
            placeholder="z. B. Rechnungen vom Lieferant" autoFocus />
        </Field>

        <div>
          <div className="text-xs font-medium text-text mb-1.5">Wenn</div>
          <div className="flex gap-2 mb-2">
            {radio("sender", "Absender enthält")}
            {radio("subject", "Betreff enthält")}
          </div>
          <Input value={condText} onChange={(e) => setCondText(e.target.value)} data-rule-cond
            placeholder={condType === "sender" ? "z. B. lieferant@firma.de" : "z. B. Rechnung"} />
        </div>

        <div>
          <div className="text-xs font-medium text-text mb-1.5">Dann</div>
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-border cursor-pointer text-sm text-text mb-2">
            <input type="checkbox" className="accent-accent-500" checked={markRead}
              onChange={(e) => setMarkRead(e.target.checked)} data-rule-markread />
            Als gelesen markieren
          </label>
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-border cursor-pointer text-sm text-text">
            <input type="checkbox" className="accent-accent-500" checked={useCategory}
              onChange={(e) => setUseCategory(e.target.checked)} data-rule-usecat />
            Kategorie zuweisen (Label)
          </label>
          {useCategory && (
            <div className="mt-2 pl-1">
              <Input value={category} onChange={(e) => setCategory(e.target.value)} data-rule-cat
                placeholder="z. B. Buchhaltung" />
            </div>
          )}
        </div>

        {!actionOk && (
          <p className="text-xs text-text-3">Mindestens eine Aktion wählen (gelesen oder Kategorie).</p>
        )}
      </div>
    </Modal>
  );
}

// Fehler eines Detail-Abrufs → lesbarer deutscher Satz. Wichtig: ein toter
// Alt-Deep-Link (entfallene uc-0xx-ID nach Neu-Bundling) liefert 404 (gültige
// UUID ohne Datensatz) ODER 422 (kein gültiges UUID im Pfad) — beide heißen
// „ID löst nicht mehr auf". FastAPI-422 trägt `detail` als Objekt-Array, dessen
// naive Stringifizierung „[object Object]" ergibt → NIE roh anzeigen.
function detailErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 404 || e.status === 422) {
      return "Diese Aufgabe existiert nicht mehr (neuer Datenstand).";
    }
    const m = (e.message || "").trim();
    return `Konnte Detail nicht laden: ${m && m !== "[object Object]" ? m : `Fehler ${e.status}`}`;
  }
  const m = e instanceof Error ? e.message.trim() : typeof e === "string" ? e.trim() : "";
  return `Konnte Detail nicht laden: ${m && m !== "[object Object]" ? m : "Unbekannter Fehler."}`;
}

// ═════════════════════════════════════════════════════════════════════════════
// L2: Detail — Was → Warum → Bestand → Empfehlung → Beweis
// ═════════════════════════════════════════════════════════════════════════════
function UseCaseDetailView({ id }: { id: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const [detail, setDetail] = useState<UseCaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDetail(null); setError(null);
    api.get<UseCaseDetail>(`/email-automation/${id}`)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(detailErrorText(e)); });
    return () => { cancelled = true; };
  }, [id]);

  // Ankersprung aus der Einzel-Ansicht: ?member=<clusterId> markiert das Muster in
  // der Evidenz-Tabelle + sortiert seine Belege nach vorn. Beim Zurück wird member
  // entfernt (Filter/Sortierung der Liste bleiben in seg/view/dept/lever/msort).
  const memberId = params.get("member");
  const backParams = new URLSearchParams(params);
  backParams.delete("member");
  const backHref = `/email-automation${backParams.toString() ? `?${backParams.toString()}` : ""}`;
  const pattern = detail?.pattern || "";
  const flow = PATTERN_FLOW[pattern] || [];

  return (
    <div>
      {/* Einheitliches Kopfband wie die Entity-Detailseiten: Zurueck + klickbarer
          Breadcrumb (Befund 5 Konsistenz). Die App-Shell traegt Titel+Tabs+Filter. */}
      <button onClick={() => navigate(backHref)} className="mb-3 inline-flex items-center gap-1.5 text-sm text-text-2 hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">
        <ArrowLeft className="w-4 h-4" /> Übersicht
      </button>
      <div className="text-[0.82rem] text-text-3 mb-4 flex items-center gap-1.5 flex-wrap">
        <Link to={backHref} className="text-brand-600 hover:text-brand-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded">Aufgaben</Link>
        <ChevronRight className="w-3 h-3 text-text-3" />
        <span className="text-text font-medium truncate max-w-[70vw]">{detail?.name ?? "Aufgabe"}</span>
      </div>

      {error && <Card padding="none" className="p-4 text-sm text-danger">{error}</Card>}
      {!error && detail === null && <div className="flex items-center gap-2 text-text-2 text-sm py-8"><Loader2 className="w-4 h-4 animate-spin" /> lädt …</div>}

      {detail && (() => {
        // Sektionen einmal aufbauen, in beiden Breakpoints wiederverwenden.
        const empfehlung = !detail.human_only ? (
          <Card padding="none" className="p-4 border-l-2 border-l-brand-500 bg-brand-50/40 dark:bg-brand-900/10">
            <div className="text-xs font-medium text-brand-500 mb-1">Empfehlung</div>
            {detail.coverage?.recommendation
              ? <p className="text-sm text-text leading-relaxed">{detail.coverage.recommendation}</p>
              : <p className="text-sm text-text-3">Keine Empfehlung hinterlegt.</p>}
            {flow.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-text-3 mb-2">{PATTERN_LABEL[pattern] || pattern}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  {flow.map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="px-2.5 py-1.5 rounded-md bg-surface-2 border border-border text-xs text-text">{step}</span>
                      {i < flow.length - 1 && <ArrowRight className="w-4 h-4 text-text-3 shrink-0" />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ) : null;

        const bestand = detail.coverage?.existing ? (
          <Card padding="none" className="p-4">
            <div className="flex items-center gap-2 mb-1"><Ampel coverage={detail.coverage} /><h2 className="text-sm font-semibold text-text">Bestand heute</h2></div>
            <p className="text-sm text-text-2 leading-relaxed">{renderExisting(detail.coverage.existing)}</p>
          </Card>
        ) : null;

        const evidenz = (
          <EvidenzCard
            members={detail.member_clusters}
            actLabels={detail.act_labels}
            counterparties={detail.counterparties}
            highlightId={memberId}
          />
        );

        // Belege des markierten Musters nach vorn ziehen (stabil sonst unverändert).
        const orderedExamples = memberId
          ? [...detail.examples].sort(
              (a, b) => Number(b.member_cluster_id === memberId) - Number(a.member_cluster_id === memberId),
            )
          : detail.examples;

        const beispiele = (
          <section>
            <h2 className="text-lg font-semibold text-text mb-3">Beispiele <span className="text-sm font-normal text-text-3">({detail.examples.length})</span></h2>
            <div className="space-y-4">
              {orderedExamples.map((e) => (
                <ExampleCard key={e.id} e={e} onError={(m) => toast.err("Fehler", m)}
                  highlight={!!memberId && e.member_cluster_id === memberId} />
              ))}
              {detail.examples.length === 0 && <div className="text-sm text-text-3">Keine Beispiele vorhanden.</div>}
            </div>
          </section>
        );

        // Doppel-Kennzahl fürs Detail: Aufwand = Σ Member-Handling (deckungsgleich
        // mit dem Bündelwert), davon autom. = hours_per_year (bereits Jahr).
        const handling6mo = (detail.member_clusters ?? []).reduce(
          (s, m) => s + (m.handling_hours_6mo ?? 0), 0,
        );
        const handlingYear = Math.round(handling6mo * 2);
        const autoYear = Math.round(detail.hours_per_year ?? 0);

        return (
          <div>
            {/* 1. Header (volle Breite) */}
            <header className="mb-5">
              <div className="flex items-center gap-2 flex-wrap">
                <PlatformBadge platform={detail.platform} />
                <DeptChip dept={detail.dept} />
                <Ampel coverage={detail.coverage} />
                <EffortBadge effort={detail.effort} />
                {detail.pattern && <span className="text-xs px-2 py-0.5 rounded-full bg-surface-3 text-text-2">{detail.pattern}</span>}
              </div>
              <h1 className="mt-2 text-2xl font-semibold text-text">{detail.name}</h1>
              <div className="mt-1 text-sm text-text-2 flex flex-wrap gap-x-3 gap-y-0.5">
                <span><span className="text-text font-medium tabular-nums">{detail.freq_6mo ?? "—"}</span> Vorgänge/6M</span>
                <span>·</span>
                <span title="Volle Handling-Zeit heute (kalibriert: S≈5, M≈17,5, L≈60 min je Vorgang, + Zuschlag bei Experten-/regulierten Aufgaben).">
                  Aufwand <span className="text-text font-semibold tabular-nums">≈ {handlingYear.toLocaleString("de-DE")}</span> h/Jahr
                </span>
                <span>·</span>
                <span title="Der nach Hebel-Konfidenz automatisierbare Anteil des Aufwands.">
                  davon autom. <span className="text-brand-500 font-semibold tabular-nums">≈ {autoYear.toLocaleString("de-DE")}</span> h/Jahr
                </span>
              </div>
              {detail.description && <p className="mt-3 text-sm text-text leading-relaxed">{detail.description}</p>}
            </header>

            {/* Ab xl: zwei Spalten (links ~60% Analyse, rechts ~40% Beispiele
                eigenständig scrollbar/sticky). Unter xl: gestapelt in der
                Reihenfolge Empfehlung · Beispiele · Bestand · Evidenz. */}
            {/* < xl: gestapelt */}
            <div className="space-y-5 xl:hidden">
              {empfehlung}
              {beispiele}
              {bestand}
              {evidenz}
            </div>

            {/* ≥ xl: zwei Spalten */}
            <div className="hidden xl:grid xl:grid-cols-5 xl:gap-6 xl:items-start">
              <div className="xl:col-span-3 space-y-5">
                {empfehlung}
                {bestand}
                {evidenz}
              </div>
              <div className="xl:col-span-2 xl:sticky xl:top-2 xl:max-h-[calc(100vh-1rem)] xl:overflow-y-auto xl:pr-1">
                {beispiele}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/** `Send_OC`/`Flow-Namen` in `existing` als <code>-Chips rendern (heuristisch:
 *  Tokens mit Underscore/CamelCase). Rest bleibt Text. */
function renderExisting(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  const re = /([A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+|\/v1\/[A-Za-z0-9/_-]+)/g;
  let last = 0; let m: RegExpExecArray | null; let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(<code key={key++} className="px-1 py-0.5 rounded bg-surface-3 text-text text-[0.8em]">{m[0]}</code>);
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ── Evidenz: Member-Cluster (Top 8 + Toggle) + Gegenparteien ─────────────────
// Zeigt die 411 eindeutigen Muster (name_de, EN als Tooltip) mit eigener Bestand-
// Ampel. Kommt der Aufruf aus der Einzel-Ansicht (?member=<clusterId>), wird die
// betreffende Zeile hervorgehoben und ins Sichtfeld gescrollt (bei Bedarf ausklappen).
const EVIDENZ_TOP = 8;
function EvidenzCard({ members, actLabels, counterparties, highlightId }: {
  members: MemberCluster[] | null;
  actLabels: Record<string, string> | null;
  counterparties: string[] | null;
  highlightId?: string | null;
}) {
  const [showAll, setShowAll] = useState(false);
  const rowRef = useRef<HTMLTableRowElement | null>(null);
  // Nach Vorgängen (freq) absteigend sortiert; stabil kopiert.
  const sorted = useMemo(
    () => [...(members ?? [])].sort((a, b) => (b.freq ?? 0) - (a.freq ?? 0)),
    [members],
  );
  const hidden = Math.max(0, sorted.length - EVIDENZ_TOP);
  const highlightIdx = highlightId ? sorted.findIndex((c) => c.id === highlightId) : -1;
  // Liegt das markierte Muster im eingeklappten Bereich → automatisch ausklappen.
  useEffect(() => { if (highlightIdx >= EVIDENZ_TOP) setShowAll(true); }, [highlightIdx]);
  const visible = showAll ? sorted : sorted.slice(0, EVIDENZ_TOP);
  // Nach dem Rendern zur markierten Zeile scrollen.
  useEffect(() => {
    if (highlightIdx < 0) return;
    const t = setTimeout(
      () => rowRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
      140,
    );
    return () => clearTimeout(t);
  }, [highlightIdx, showAll]);

  return (
    <Card padding="none" className="p-4">
      <h2 className="text-sm font-semibold text-text mb-3">Evidenz <span className="text-xs font-normal text-text-3">— die übernommenen Aufgaben (deutsch) mit eigener Bestand-Ampel</span></h2>
      {sorted.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-text-3 text-left text-xs">
              <th className="py-1 pr-3 font-medium">Aufgabe</th>
              <th className="py-1 px-3 font-medium">Aktion</th>
              <th className="py-1 px-3 font-medium text-right w-16">n</th>
              <th className="py-1 px-3 font-medium text-right w-20" title="Aufwand je Aufgabe (volle Handling-Zeit) in Stunden / 6 Monate">Aufw. h/6M</th>
              <th className="py-1 pl-3 font-medium text-center w-14">Best.</th>
            </tr></thead>
            <tbody>
              {visible.map((c, i) => {
                const hot = highlightId != null && c.id === highlightId;
                return (
                  <tr key={c.id ?? i} ref={hot ? rowRef : undefined}
                    className={clsx("border-t border-border align-top", hot && "bg-brand-50 dark:bg-brand-500/10 ring-1 ring-inset ring-brand-500/40")}>
                    <td className="py-1.5 pr-3 text-text" title={c.name ?? c.id ?? undefined}>{c.name_de || c.name || c.id || "—"}</td>
                    <td className="py-1.5 px-3"><ActLabelChip label={actLabel(c.act, c.act_label, actLabels)} /></td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-text-2">{c.freq ?? "—"}</td>
                    <td className="py-1.5 px-3 text-right tabular-nums text-text-2">{c.handling_hours_6mo != null ? c.handling_hours_6mo.toFixed(1) : "—"}</td>
                    <td className="py-1.5 pl-3 text-center"><Ampel coverage={c.coverage ?? null} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {hidden > 0 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-text-2 hover:text-text"
            >
              {showAll ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {showAll ? "Weniger anzeigen" : `Alle ${sorted.length} anzeigen`}
            </button>
          )}
        </div>
      )}
      {counterparties && counterparties.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-text-3 mb-1">Gegenparteien</div>
          <div className="flex flex-wrap gap-1.5">
            {counterparties.map((cp, i) => <span key={i} className="px-2 py-0.5 rounded-md bg-surface-3 text-text-2 text-xs">{cp}</span>)}
          </div>
        </div>
      )}
    </Card>
  );
}

function DirectionMark({ direction }: { direction: string | null }) {
  const d = (direction || "").toLowerCase();
  if (d === "outbound") return <span className="inline-flex items-center gap-0.5 text-accent-600 dark:text-accent-300" title="ausgehend"><ArrowUpRight className="w-3.5 h-3.5" />→</span>;
  if (d === "inbound") return <span className="inline-flex items-center gap-0.5 text-brand-600 dark:text-brand-300" title="eingehend"><ArrowDownLeft className="w-3.5 h-3.5" />←</span>;
  return <span className="text-text-3">—</span>;
}

// ── Beispiel-Karte: Zitat prominent (Blockquote), Meta darunter, Anhang inline ─
export function ExampleCard({ e, onError, highlight = false }: { e: Example; onError: (m: string) => void; highlight?: boolean }) {
  const [showSummary, setShowSummary] = useState(false);
  const atts = e.attachments || [];
  const sfUrl = sfThreadUrl(e.thread_id);
  return (
    <Card padding="none" className={clsx("p-4 flex flex-col", highlight && "ring-2 ring-brand-500/50 border-brand-500")}>
      {/* Kontext: welchem Member-Cluster dieses Beispiel als Beleg zugeordnet
          ist (nur wenn im Dataset vorhanden). Bei Ankersprung aus der Einzel-
          Ansicht (highlight) wird der Beleg-Bezug hervorgehoben. */}
      {e.member_cluster_name && (
        <div className="mb-2 text-[0.7rem] text-text-3">
          {highlight
            ? <span className="font-semibold text-brand-600 dark:text-brand-300">Belegt (diese Aufgabe):</span>
            : "Belegt:"} <span className="text-text-2 font-medium">{e.member_cluster_name}</span>
        </div>
      )}
      {/* Zitat prominent */}
      <blockquote className="relative pl-6 text-sm text-text leading-relaxed">
        <Quote className="w-4 h-4 text-brand-400 absolute left-0 top-0.5" />
        <span className="italic">{e.quote || "(kein Zitat)"}</span>
      </blockquote>

      {/* Meta: Betreff · Datum · Richtung · Mailbox */}
      <div className="mt-3 text-xs text-text-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        {e.subject && <span className="text-text-2 font-medium truncate max-w-full">{e.subject}</span>}
        <span>·</span>
        <span>{fmtDate(e.occurred_at)}</span>
        <span>·</span>
        <DirectionMark direction={e.direction} />
        {e.mailbox && <><span>·</span><span className="truncate">{e.mailbox}</span></>}
        {sfUrl && (
          <>
            <span>·</span>
            <a href={sfUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300 text-[0.68rem] font-semibold hover:bg-brand-500 hover:text-white transition-colors"
              title="Salesforce-Thread in neuem Tab öffnen">
              SF-Thread <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </>
        )}
      </div>

      {/* SF-Thread-Summary hinter Toggle */}
      {e.sf_thread_summary && (
        <div className="mt-2">
          <button onClick={() => setShowSummary((v) => !v)} className="inline-flex items-center gap-1 text-xs text-text-2 hover:text-text">
            {showSummary ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            SF-Thread-Zusammenfassung
          </button>
          {showSummary && <p className="mt-1.5 text-xs text-text-2 leading-relaxed bg-surface-2 rounded-md p-2">{e.sf_thread_summary}</p>}
        </div>
      )}

      {/* Anhang inline — nur wenn vorhanden */}
      {atts.length > 0 && (
        <div className="mt-3">
          <div className="text-xs text-text-3 mb-1.5">Anhang</div>
          <div className="space-y-2">{atts.map((a) => <AttachmentPreview key={a.id} att={a} onError={onError} />)}</div>
        </div>
      )}
    </Card>
  );
}

// ── Inline-Anlage: Chip → Vorschau (PDF im iframe / Bild) on-demand ──────────
export function AttachmentPreview({ att, onError }: { att: Attachment; onError: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const urlRef = useRef<string | null>(null);
  const ext = extOf(att.filename);
  const isPdf = ext === "pdf";
  const isImg = IMG_EXT.includes(ext);
  const previewable = isPdf || isImg;

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  async function ensureBlob(): Promise<string | null> {
    if (urlRef.current) return urlRef.current;
    setLoading(true);
    try {
      const t = authToken();
      const r = await fetch(apiUrl(`/email-automation/attachment/${att.id}`), { headers: t ? { Authorization: `Bearer ${t}` } : undefined });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      urlRef.current = u; setUrl(u); return u;
    } catch (e) { onError((e as Error).message); return null; }
    finally { setLoading(false); }
  }
  async function toggle() { if (!open && previewable) await ensureBlob(); setOpen((v) => !v); }
  async function download() {
    const u = await ensureBlob(); if (!u) return;
    const a = document.createElement("a"); a.href = u; a.download = att.filename || "anlage";
    document.body.appendChild(a); a.click(); a.remove();
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-surface-2">
        {isImg ? <ImageIcon className="w-4 h-4 text-text-3 shrink-0" /> : <FileText className="w-4 h-4 text-text-3 shrink-0" />}
        <button onClick={toggle} className="min-w-0 flex-1 text-left" title={previewable ? "Vorschau" : "Kein Inline-Preview für diesen Typ"}>
          <span className="text-xs text-text truncate block">{att.filename || "Anlage"}</span>
        </button>
        {att.size != null && <span className="text-xs text-text-3 shrink-0">{fmtBytes(att.size)}</span>}
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-3" />}
        {previewable && (
          <button onClick={toggle} className="inline-flex items-center gap-0.5 text-xs text-text-3 hover:text-text shrink-0" title="Vorschau ein-/ausklappen">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="hidden sm:inline">{open ? "verbergen" : isImg ? "Bild anzeigen" : "PDF anzeigen"}</span>
          </button>
        )}
        <button onClick={download} className="text-text-3 hover:text-text shrink-0" title="Herunterladen"><Download className="w-4 h-4" /></button>
      </div>
      {open && previewable && url && (
        <div className="bg-surface">
          {isPdf ? (
            <iframe src={url} title={att.filename || "PDF"} className="w-full h-[440px] border-0" />
          ) : (
            <div className="p-2 flex justify-center bg-surface-3"><img src={url} alt={att.filename || "Bild"} className="max-h-[440px] max-w-full object-contain" /></div>
          )}
          <div className="px-2.5 py-1 border-t border-border">
            <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-text-3 hover:text-text"><ExternalLink className="w-3 h-3" /> in neuem Tab öffnen</a>
          </div>
        </div>
      )}
      {open && !previewable && <div className="px-2.5 py-2 text-xs text-text-3">Kein Inline-Preview für <code>.{ext || "?"}</code> — bitte herunterladen.</div>}
    </div>
  );
}
